import produce from 'immer';
import reactUpdate, {Spec} from 'immutability-helper';

import {assert} from 'Asserts';
import Editor from 'Editor';
import {getTablePlugin} from 'notebook/notes/plugins/getTablePlugin';
import {getTableNotePartIndex} from 'notebook/tables/store/selectors/TableSelectors';
import {Cell, Table, TableIndex} from 'notebook/tables/TableConstants';
import {sanitizeTypedValueForSave} from 'notebook/tables/typed-values/DataTypes';
import {Note, NotePart} from 'notebook/util/NoteUtilConstants';
import {omit} from 'util/omitKey';

export function updateTableInNote(note: Note, table: Table): Note {
  const notePartIndex = getTableNotePartIndex(note, table.name);
  assert(notePartIndex != null, `Note part index not found for ${table.name}`);
  const notePart = note.noteParts[notePartIndex];
  const plugin = getTablePlugin(notePart);
  // Neither the table or structured table plugins use this editor instance, so it does not need to be real.
  const updatedNotePartSpec = plugin.setContent.call({} as Editor, {
    table,
  }) as Spec<NotePart>;
  return reactUpdate(note, {
    noteParts: {[notePartIndex]: updatedNotePartSpec},
  });
}

export function updateCellInTable(
  table: Table,
  [rowIndex, cellIndex]: TableIndex,
  cellData: Partial<Cell>
): Table {
  let oldCell = table.table[rowIndex][cellIndex];
  // Any usage of cell.error should be computed and automatically reapplied by the store if still applicable
  oldCell = omit(oldCell, 'error', 'typedValue');
  const newCell: Cell = {...oldCell, ...cellData};
  if (cellData.typedValue != null) {
    newCell.typedValue = sanitizeTypedValueForSave(cellData.typedValue);
  }
  const updatedTable = produce(table, (draftTable) => {
    draftTable.table[rowIndex][cellIndex] = newCell;
  });
  return updatedTable;
}
